import { useEffect, useMemo, useRef, useState } from "react";

import type { DenetimSummary } from "@/api/denetim";
import { ApiError } from "@/api/client";
import {
  isTerminalJobStatus,
  useDenetimKategoriler,
  useInvalidateKatalog,
  useJob,
  useKatalog,
  useKatalogTara,
} from "@/api/denetim";
import { fetchJobs, type JobOut } from "@/api/sync";
import { useAuth } from "@/auth";
import { EmptyState } from "@/components/EmptyState";
import { SortableTable } from "@/components/SortableTable";
import { Button } from "@/components/ui";
import { getBulguColumns } from "@/features/denetim/columns";
import { buildDenetimCsv, denetimCsvDosyaAdi, triggerCsvDownload } from "@/features/denetim/csv";
import { DegerDagilimTablosu } from "@/features/denetim/DegerDagilimTablosu";
import { DenetimOzet } from "@/features/denetim/DenetimOzet";
import { DENETIM_KURAL_VARSAYILAN, loadDenetimKural, saveDenetimKural } from "@/features/denetim/kuralForm";
import { KuralPaneli } from "@/features/denetim/KuralPaneli";
import { filterBySiddet, flattenFindings } from "@/features/denetim/rows";
import type { Siddet } from "@/features/denetim/severity";
import { SiddetFiltre } from "@/features/denetim/SiddetFiltre";
import { t } from "@/i18n";
import { fmtDateTime } from "@/lib/utils";

/** GET yanıtındaki `summary.kurallar` (yoksa `summary.kural` — ikisi de AYNI
 * nesne, bkz. app/core/audit.py run_catalog_audit) → kural özet satırı
 * ("Kurallar: kelime=…, yüzde=…") ve dağılım tablosu başlığı için alanlar. */
function kuralDenBul(summary: DenetimSummary): Record<string, unknown> | null {
  const kural = summary["kurallar"] ?? summary["kural"];
  return kural && typeof kural === "object" ? (kural as Record<string, unknown>) : null;
}

function kuralOzetMetni(summary: DenetimSummary): string | null {
  const kural = kuralDenBul(summary);
  if (!kural) return null;
  return t("denetim.kural.ozet_satiri", {
    kelime: typeof kural.kelime === "string" ? kural.kelime : "—",
    yuzde: typeof kural.yuzde === "number" ? kural.yuzde : "—",
    ozellik: typeof kural.ozellik_adi === "string" ? kural.ozellik_adi : "—",
    rozet: typeof kural.rozet_dosyasi === "string" && kural.rozet_dosyasi ? kural.rozet_dosyasi : "—",
  });
}

function dagilimOf(summary: DenetimSummary): Record<string, number> {
  const raw = summary["dagilim"];
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, number> = {};
  for (const [deger, adet] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof adet === "number") out[deger] = adet;
  }
  return out;
}

function ozellikAdiOf(summary: DenetimSummary): string {
  const kural = kuralDenBul(summary);
  return typeof kural?.ozellik_adi === "string" ? kural.ozellik_adi : DENETIM_KURAL_VARSAYILAN.ozellik_adi;
}

function hataMesaji(error: unknown): string {
  if (error instanceof ApiError) return error.detail && typeof error.detail === "string" ? error.detail : error.message;
  return t("common.error");
}

/**
 * `core.jobs.result` — `jobSchema` (api/sync.ts) `.passthrough()` ile TUM
 * bilinmeyen alanlari `unknown` tasir, `catalog_audit` isine ozel bir sema
 * YOKTUR (kurulum/useConnectionTestStatus.ts'in `connectionTestJobSchema`
 * genislemesinin aksine — burada tek kullanim yeri oldugundan ayri bir zod
 * semasi yerine kucuk bir tip daraltici yeterli). `run_catalog_audit`
 * (app/core/audit.py) basarili TAMAMLANMIS bir job icin bile
 * `{"status":"error","hata":"..."}` DONEBILIR (ör. urun kartlari henuz sync
 * edilmedi) — bu, job'in KENDI `status`'u 'done' oldugu halde domain
 * seviyesinde bir hatadir.
 */
interface CatalogAuditResult {
  status?: string;
  hata?: string;
}

function auditResultOf(job: JobOut | undefined): CatalogAuditResult | null {
  const result = (job as { result?: unknown } | undefined)?.result;
  return result && typeof result === "object" ? (result as CatalogAuditResult) : null;
}

/**
 * /denetim — katalog denetimi + CSV (docs/faz5-arayuz-spec.md §2). "Yeniden
 * tara" `POST .../tara` → job_id → useJob polling (5sn, sekme gizliyken durur)
 * → terminal durumda katalog GET yeniden yuklenir.
 */
export function Denetim() {
  const katalogQuery = useKatalog();
  const taraMutation = useKatalogTara();
  const invalidateKatalog = useInvalidateKatalog();
  const kategoriQuery = useDenetimKategoriler();
  const { principal } = useAuth();

  const [jobId, setJobId] = useState<number | null>(null);
  const [siddetFiltre, setSiddetFiltre] = useState<Siddet | "hepsi">("hepsi");
  // Kural paneli (görev A) — son kullanılan kural localStorage'dan yüklenir
  // (asakai.denetim.kurallar), her değişiklikte geri yazılır (aşağıdaki useEffect).
  const [kural, setKural] = useState(() => loadDenetimKural());
  // Backend dedupe_key ile ayni tenant icin ikinci bir tarama isi
  // kuyruklamaz (`job_id: null` doner, jobs.py enqueue()) — bu bayrak o
  // durumda kullaniciya "zaten suruyor" bilgisini gosterir.
  const [dedupeNotice, setDedupeNotice] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const handledJobRef = useRef<number | null>(null);

  const jobResult = useJob(jobId);
  const columns = useMemo(() => getBulguColumns(), []);

  const canTara = principal?.role === "tenant_admin" || Boolean(principal?.user.is_super);

  useEffect(() => {
    if (jobId == null) return;
    const job = jobResult.job;
    const status = job?.status;
    if (status && isTerminalJobStatus(status) && handledJobRef.current !== jobId) {
      handledJobRef.current = jobId;
      // Job'un KENDI durumu 'done' olsa bile domain sonucu hata olabilir
      // (run_catalog_audit {"status":"error","hata":...} donebilir).
      const result = auditResultOf(job);
      if (status === "done" && result?.status === "error") {
        setScanError(result.hata ?? t("denetim.tara.sonuc_hata"));
      } else {
        setScanError(null);
      }
      setDedupeNotice(false);
      void invalidateKatalog();
      setJobId(null);
    }
  }, [jobId, jobResult.job, invalidateKatalog]);

  useEffect(() => {
    saveDenetimKural(kural);
  }, [kural]);

  async function yenidenTara() {
    setScanError(null);
    setDedupeNotice(false);
    taraMutation.mutate(kural, {
      onSuccess: async (res) => {
        if (res.job_id != null) {
          setJobId(res.job_id);
          return;
        }
        // job_id null (dedupe): ayni tenant icin ZATEN kuyrukta/calisan bir
        // tarama var — /jobs listesinden en son 'catalog_audit' isini bulup
        // TAKIP EDERIZ (useConnectionTestStatus.ts ile ayni desen), aksi
        // halde kullanici hicbir geri bildirim gormeden dugme normale doner.
        setDedupeNotice(true);
        try {
          const { jobs } = await fetchJobs(20);
          const existing = jobs.find((j) => j.kind === "catalog_audit");
          if (existing) setJobId(existing.id);
        } catch {
          // /jobs sorgusu basarisiz olursa sessiz kal — "Yeniden tara"
          // butonu tekrar kullanilabilir kalir (jobId null -> isScanning false).
        }
      },
    });
  }

  if (katalogQuery.isLoading) {
    return <p className="text-sm text-muted">{t("common.loading")}</p>;
  }
  if (katalogQuery.isError) {
    return (
      <p role="alert" className="text-sm text-red">
        {t("common.error")}
      </p>
    );
  }

  const data = katalogQuery.data;
  if (!data) return null;
  if (data.status === "not_supported") {
    return <EmptyState kind="not_supported" />;
  }

  const isReady = data.status === "ready";
  const summary = isReady ? data.summary : {};
  const findings = isReady ? data.findings : {};
  const runAt = isReady ? (data.run_at ?? null) : null;

  const rows = flattenFindings(findings);
  const filteredRows = filterBySiddet(rows, siddetFiltre);
  const isScanning = jobId != null && !isTerminalJobStatus(jobResult.job?.status);

  function csvIndir() {
    triggerCsvDownload(buildDenetimCsv(filteredRows), denetimCsvDosyaAdi());
  }

  return (
    <section className="space-y-6">
      <div>
        <h1 className="font-display text-xl font-bold text-navy">{t("nav.denetim")}</h1>
        <p className="text-xs text-muted">
          {runAt ? t("denetim.son_tarama", { zaman: fmtDateTime(runAt) }) : t("denetim.hic_taranmadi")}
        </p>
      </div>

      {canTara && (
        <KuralPaneli
          value={kural}
          onChange={setKural}
          kategoriSecenekleri={kategoriQuery.data ?? []}
          disabled={taraMutation.isPending || isScanning}
        />
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {canTara && (
          <Button variant="primary" onClick={() => void yenidenTara()} disabled={taraMutation.isPending || isScanning}>
            {isScanning ? t("denetim.tara.calisiyor") : t("denetim.tara.buton")}
          </Button>
        )}
        {isReady && (
          <Button variant="outline" onClick={csvIndir} disabled={filteredRows.length === 0}>
            {t("denetim.csv.buton")}
          </Button>
        )}
        {isReady && (
          <Button variant="outline" onClick={() => window.print()}>
            {t("denetim.yazdir")}
          </Button>
        )}
      </div>

      {taraMutation.isError && (
        <p role="alert" className="text-sm text-red">
          {hataMesaji(taraMutation.error)}
        </p>
      )}

      {dedupeNotice && (
        <p role="status" className="text-sm text-muted">
          {t("denetim.tara.zaten_suruyor")}
        </p>
      )}

      {scanError && (
        <p role="alert" className="text-sm text-red">
          {scanError}
        </p>
      )}

      {!isReady ? (
        <EmptyState kind="no_data" />
      ) : (
        <>
          <DenetimOzet summary={summary} />
          {kuralOzetMetni(summary) && (
            <p className="text-xs text-muted">{kuralOzetMetni(summary)}</p>
          )}
          <SiddetFiltre value={siddetFiltre} onChange={setSiddetFiltre} />
          {filteredRows.length === 0 ? (
            <p className="text-sm text-muted">{t("denetim.bulgu.bos")}</p>
          ) : (
            <SortableTable columns={columns} rows={filteredRows} rowKey={(r) => r.id} initialSortKey="siddet" stickyFirstColumn />
          )}
          <DegerDagilimTablosu ozellikAdi={ozellikAdiOf(summary)} dagilim={dagilimOf(summary)} />
        </>
      )}
    </section>
  );
}
